import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Users,
  Layers,
  CalendarRange,
  Dumbbell,
  Library,
  BookOpen,
  CalendarDays,
  Menu,
  X,
  ChevronLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CoachLayoutProps {
  children: ReactNode;
}

const coachNav = [
  { path: '/coach/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/coach/athletes', label: 'Athletes', icon: Users },
  { path: '/coach/plans', label: 'Plan Builder', icon: Layers },
  { path: '/coach/week-builder', label: 'Week Builder', icon: CalendarRange },
  { path: '/coach/workouts', label: 'Workout Builder', icon: Dumbbell },
  { path: '/coach/library', label: 'Template Library', icon: Library },
  { path: '/coach/knowledge', label: 'Knowledge Base', icon: BookOpen },
  { path: '/coach/calendar', label: 'Plan Calendar', icon: CalendarDays },
];

export function CoachLayout({ children }: CoachLayoutProps) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--app-bg)' }}>
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:sticky top-0 left-0 z-40 h-screen w-60 flex-shrink-0 flex flex-col border-r border-border/10 transition-transform lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        style={{ background: 'rgba(11, 14, 20, 0.97)', backdropFilter: 'blur(20px)' }}
      >
        {/* Header */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-border/10">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-accent/20 flex items-center justify-center">
              <span className="text-[10px] font-mono font-bold text-accent tracking-tighter">AS</span>
            </div>
            <span className="text-xs font-mono uppercase tracking-[0.12em] text-foreground/70">Coach</span>
          </div>
          <Button variant="ghost" size="icon" className="lg:hidden text-muted-foreground" onClick={() => setMobileOpen(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Back to athlete view */}
        <Link
          to="/today"
          className="flex items-center gap-2 px-4 py-2.5 text-[10px] font-mono uppercase tracking-[0.1em] text-muted-foreground hover:text-foreground/80 border-b border-border/10 transition-colors"
        >
          <ChevronLeft className="h-3 w-3" />
          Athlete View
        </Link>

        {/* Nav */}
        <nav className="flex-1 py-3 px-2 space-y-0.5">
          {coachNav.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-[11px] font-mono uppercase tracking-[0.08em] transition-all duration-150',
                  isActive
                    ? 'text-accent bg-accent/10 border border-accent/20'
                    : 'text-muted-foreground hover:text-foreground/70 hover:bg-white/[0.03]',
                )}
              >
                <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={isActive ? 1.8 : 1.2} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-20 h-12 flex items-center gap-3 px-4 border-b border-border/10" style={{ background: 'rgba(11, 14, 20, 0.95)', backdropFilter: 'blur(16px)' }}>
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} className="text-muted-foreground">
            <Menu className="h-4 w-4" />
          </Button>
          <span className="text-xs font-mono uppercase tracking-[0.12em] text-muted-foreground">Coach Studio</span>
        </header>

        <main className="max-w-[1400px] mx-auto px-4 py-6 lg:px-8 lg:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}
